"use client";

import { ChevronLeft, ChevronRight, X, Search } from "lucide-react";
import Image from "next/image";
import { useCallback, useEffect, useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { useLanguage } from "@/hooks/useLanguage";
import { translations } from "@/lib/translations";

interface SearchToken {
	id: string;
	name: string;
	ticker: string;
	image: string;
	price: number;
	verified: boolean;
	featured: boolean;
	holder_count: number;
	comment_count: number;
}

interface SearchResponse {
	data: SearchToken[];
	page: number;
	limit: number;
	count: number;
}

interface ViewSearchModalProps {
	isOpen: boolean;
	onClose: () => void;
	onSelect: (tokenId: string) => void;
}

export function ViewSearchModal({
	isOpen,
	onClose,
	onSelect,
}: ViewSearchModalProps) {
	const [searchQuery, setSearchQuery] = useState("");
	const [results, setResults] = useState<SearchToken[]>([]);
	const [currentPage, setCurrentPage] = useState(1);
	const [totalCount, setTotalCount] = useState(0);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const { language } = useLanguage();
	const t = (translations[language] as any).view;
	const limit = 5;

	const fetchSearchResults = useCallback(
		async (query: string, page: number) => {
			if (!query.trim()) {
				setResults([]);
				setTotalCount(0);
				return;
			}

			setLoading(true);
			setError(null);

			try {
				const response = await fetch(
					`https://api.odin.fun/v1/tokens?page=${page}&limit=${limit}&sort=price%3Adesc&search=${encodeURIComponent(query)}`,
				);

				if (!response.ok) {
					throw new Error("Search failed");
				}

				const data: SearchResponse = await response.json();
				setResults(data.data);
				setTotalCount(data.count);
			} catch (err) {
				setError(err instanceof Error ? err.message : "Search error");
				setResults([]);
				setTotalCount(0);
			} finally {
				setLoading(false);
			}
		},
		[limit],
	);

	useEffect(() => {
		const timer = setTimeout(() => {
			if (isOpen && searchQuery) {
				setCurrentPage(1);
				fetchSearchResults(searchQuery, 1);
			}
		}, 300);
		return () => clearTimeout(timer);
	}, [fetchSearchResults, isOpen, searchQuery]);

	useEffect(() => {
		if (isOpen && searchQuery && currentPage > 1) {
			fetchSearchResults(searchQuery, currentPage);
		}
	}, [currentPage, fetchSearchResults, isOpen, searchQuery]);

	const formatPrice = (price: number) => {
		return `${(price / 1000).toLocaleString()} sats`;
	};

	const totalPages = Math.ceil(totalCount / limit);

	return (
		<Dialog open={isOpen} onOpenChange={onClose}>
			<DialogContent className="max-w-md h-[80vh] flex flex-col bg-light-surface dark:bg-dark-surface border-light-border dark:border-dark-border [&>button]:hidden p-0 overflow-hidden">
				<div className="p-4 border-b border-light-border dark:border-dark-border">
					<div className="flex items-center justify-between mb-4">
						<h2 className="text-lg font-bold text-light-text dark:text-dark-text">
							{t.setting}
						</h2>
						<Button
							variant="ghost"
							size="sm"
							onClick={onClose}
							className="h-8 w-8 p-0"
						>
							<X className="h-4 w-4" />
						</Button>
					</div>
					<div className="relative">
						<Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
						<Input
							placeholder={t.searchPlaceholder}
							className="pl-9 bg-light-background dark:bg-dark-background"
							value={searchQuery}
							onChange={(e) => setSearchQuery(e.target.value)}
							autoFocus
						/>
					</div>
				</div>

				<div className="flex-1 overflow-y-auto p-4 min-h-0">
					{loading && results.length === 0 ? (
						<div className="flex items-center justify-center py-8">
							<div className="animate-pulse text-gray-500">Searching...</div>
						</div>
					) : results.length === 0 ? (
						<div className="flex flex-col items-center justify-center py-12 text-gray-500 opacity-50">
							<Search className="h-12 w-12 mb-2" />
							<p>{searchQuery ? "No results found" : "Search for a token"}</p>
						</div>
					) : (
						<div className="space-y-2">
							{results.map((token) => (
								<div
									key={token.id}
									className="flex items-center justify-between p-3 rounded-xl bg-light-background dark:bg-dark-background hover:bg-light-primary/5 dark:hover:bg-dark-primary/5 transition-colors cursor-pointer border border-transparent hover:border-light-primary/20"
									onClick={() => onSelect(token.id)}
								>
									<div className="flex items-center gap-3 flex-1 min-w-0">
										<Image
											src={`https://images.odin.fun/token/${token.id}`}
											alt={token.name}
											width={40}
											height={40}
											className="rounded-full flex-shrink-0"
										/>
										<div className="flex-1 min-w-0">
											<div className="flex items-center gap-2">
												<h3 className="font-bold text-light-text dark:text-dark-text truncate">
													{token.ticker}
												</h3>
												{token.verified && (
													<Badge className="bg-status-success h-4 w-4 p-0 flex items-center justify-center text-[10px] text-white">
														✓
													</Badge>
												)}
											</div>
											<p className="text-xs text-gray-500 truncate">
												{token.name}
											</p>
										</div>
										<div className="text-right">
											<div className="text-xs font-medium text-light-accent dark:text-dark-accent">
												{formatPrice(token.price)}
											</div>
											<div className="text-[10px] text-gray-500">
												MCap {token.holder_count} holders
											</div>
										</div>
									</div>
								</div>
							))}
						</div>
					)}
				</div>

				{totalPages > 1 && (
					<div className="flex items-center justify-center gap-2 p-4 border-t border-light-border dark:border-dark-border">
						<Button
							variant="outline"
							size="icon"
							className="h-8 w-8"
							onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
							disabled={currentPage === 1}
						>
							<ChevronLeft className="h-4 w-4" />
						</Button>
						<span className="text-sm font-medium">
							{currentPage} / {totalPages}
						</span>
						<Button
							variant="outline"
							size="icon"
							className="h-8 w-8"
							onClick={() => setCurrentPage((prev) => Math.min(totalPages, prev + 1))}
							disabled={currentPage === totalPages}
						>
							<ChevronRight className="h-4 w-4" />
						</Button>
					</div>
				)}
			</DialogContent>
		</Dialog>
	);
}
